import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import { ConfigProvider } from 'antd';
import React, { FC, useMemo } from 'react';
import { Button } from 'components/atoms/button';
import { Option, UserTag } from 'components/atoms/select';
import { useTranslation } from 'hooks/use-translation';
import { byLabel } from 'utils/comparators/by-label';
import { getRandomColor } from 'utils/get-random-color';
import { RoleAndActionColumn, UsersTable } from './components';
import { emptyState } from './empty-state';
import { TimesheetExcludedUsers } from './types';
import { useRemoveExcludedUsers } from './use-remove';
import { asNonRemovedList, isInAdding } from './utils';

interface Props {
  excludedUsers: TimesheetExcludedUsers;
  allUserOptions: Option[];
  isUserOptionsLoading: boolean;
}
export const ExcludedUserTable: FC<Props> = ({
  excludedUsers,
  allUserOptions,
  isUserOptionsLoading,
}) => {
  const excludedUserIds = useMemo(
    () => asNonRemovedList(excludedUsers),
    [excludedUsers],
  );
  const dataSource = useMemo(
    () =>
      excludedUserIds
        .map((id) => allUserOptions.find((x) => x.value === id))
        .filter(Boolean)
        .sort(byLabel),
    [excludedUserIds, allUserOptions],
  );
  const removeUsers = useRemoveExcludedUsers();

  return (
    <ConfigProvider renderEmpty={emptyState}>
      <UsersTable
        size="small"
        pagination={false}
        dataSource={dataSource}
        loading={excludedUsers[0] === 'loading' || isUserOptionsLoading}
        rowKey="value"
        scroll={{ y: '570px' }}
        rowClassName={(option) =>
          isInAdding(Number(option.value))(excludedUsers) ? 'highlight' : ''
        }
        columns={[
          {
            title: useTranslation('timesheets.column.team_members', {
              count: dataSource.length,
            }),
            dataIndex: 'label',
            render: (value, option) => (
              <UserTag
                color={getRandomColor(option.value)}
                imageUrl={option.imageUrl}
                label={value}
              />
            ),
          },
          {
            title: useTranslation('role'),
            dataIndex: 'description',
            render: (value, option) => {
              return (
                <RoleAndActionColumn>
                  <span>{value}</span>
                  <Button
                    icon={<TrashCan16 />}
                    type="text"
                    onClick={() => removeUsers([Number(option.value)])}
                  />
                </RoleAndActionColumn>
              );
            },
          },
        ]}
      />
    </ConfigProvider>
  );
};
